import { AssetVariantDTO } from "../../dto/AssetDTO";
import { LanguageData } from "../../app/slices/TrainingLocalizationSlice";
import { languageCodesEqual } from "./languageCode";

/** The variant every other one falls back to. It has no language and cannot get one. */
export const DEFAULT_VARIANT = "default";

/** The placeholder row the upload hook prepends while a file is in flight. */
export const LOADING_VARIANT_ID = "loading";

export type VariantKind = "default" | "assigned" | "unassigned" | "loading";

export const variantKind = (
  variant: AssetVariantDTO,
  languages: LanguageData[]
): VariantKind => {
  if (variant.id === LOADING_VARIANT_ID) return "loading";
  if (variant.variant === DEFAULT_VARIANT) return "default";
  return languages.some((l) => languageCodesEqual(l.shortcode, variant.variant))
    ? "assigned"
    : "unassigned";
};

export const variantLanguage = (
  variant: AssetVariantDTO,
  languages: LanguageData[]
) => languages.find((l) => languageCodesEqual(l.shortcode, variant.variant));

/**
 * A filename for a variant, which the DTO does not carry directly — only a
 * blob path. Returns undefined rather than a half-truth when the last segment
 * does not look like a file, so callers can leave the line out entirely.
 */
export const variantFileName = (variant: AssetVariantDTO) => {
  const segment = (variant.blobPath ?? "").split(/[\\/]/).pop();
  return segment && segment.includes(".") ? segment : undefined;
};

/**
 * Default first, then the languages in the order the API returned them, then
 * anything still waiting for a language. Unassigned files sort last because
 * they are the only rows that need an action — they should not be scattered
 * among the finished ones, and the list is short enough to scan.
 */
export const orderVariants = (
  variants: AssetVariantDTO[],
  languages: LanguageData[]
) => {
  const rank = (v: AssetVariantDTO) => {
    const kind = variantKind(v, languages);
    if (kind === "default") return 0;
    if (kind === "assigned") return 1;
    return 2; // loading and unassigned both belong at the end
  };

  return [...variants].sort((a, b) => rank(a) - rank(b));
};
